/**
 * Profile settings tab - user profile management
 */
import { useState } from 'react'
import { useUserProfile, useUpdateUserProfile, useOnboardingProgress } from '../../hooks'
import { Button, Input, LoadingCard } from '../common'

export function ProfileTab() {
  const { data: profile, isLoading } = useUserProfile()
  const updateProfile = useUpdateUserProfile()
  const { isEnrolled, reset: resetOnboarding } = useOnboardingProgress()

  const [displayName, setDisplayName] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const [error, setError] = useState('')
  const [onboardingReset, setOnboardingReset] = useState(false)

  const handleResetOnboarding = () => {
    console.log('Reset onboarding clicked')
    resetOnboarding()
    setOnboardingReset(true)
  }

  const startEditing = () => {
    setDisplayName(profile?.display_name || '')
    setIsEditing(true)
    setError('')
  }

  const cancelEditing = () => {
    setIsEditing(false)
    setError('')
  }

  const handleSave = async () => {
    setError('')
    try {
      await updateProfile.mutateAsync({ display_name: displayName.trim() || undefined })
      setIsEditing(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile')
    }
  }

  if (isLoading) {
    return <LoadingCard />
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never'
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div className="space-y-6">
      {/* Profile Info */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Profile Information</h2>
        </div>
        <div className="px-6 py-4 space-y-4">
          {/* Display Name */}
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700">Display Name</label>
              {isEditing ? (
                <div className="mt-1 flex items-center gap-2">
                  <Input
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Enter display name"
                    className="max-w-xs"
                  />
                  <Button size="sm" onClick={handleSave} isLoading={updateProfile.isPending}>
                    Save
                  </Button>
                  <Button size="sm" variant="secondary" onClick={cancelEditing}>
                    Cancel
                  </Button>
                </div>
              ) : (
                <div className="mt-1 flex items-center gap-4">
                  <span className="text-sm text-gray-900">
                    {profile?.display_name || <span className="text-gray-400">Not set</span>}
                  </span>
                  <Button size="sm" variant="secondary" onClick={startEditing}>
                    Edit
                  </Button>
                </div>
              )}
              {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
            </div>
          </div>

          {/* Email (read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <p className="mt-1 text-sm text-gray-900">{profile?.email || 'Not available'}</p>
            <p className="mt-1 text-xs text-gray-500">Email is managed through your login provider</p>
          </div>

          {/* Tenant */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Organization</label>
            <p className="mt-1 text-sm text-gray-900">{profile?.tenant_name || 'Unknown'}</p>
          </div>
        </div>
      </div>

      {/* Account Info */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Account Details</h2>
        </div>
        <div className="px-6 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">User ID</label>
            <p className="mt-1 text-sm text-gray-500 font-mono">{profile?.id}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Account Created</label>
            <p className="mt-1 text-sm text-gray-900">{formatDate(profile?.created_at || null)}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Last Login</label>
            <p className="mt-1 text-sm text-gray-900">{formatDate(profile?.last_login || null)}</p>
          </div>
        </div>
      </div>

      {/* Onboarding */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Getting Started</h2>
        </div>
        <div className="px-6 py-4">
          {onboardingReset ? (
            <div className="flex items-center gap-2 text-green-600">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-sm">Guide reset! Visit the Dashboard to see it again.</span>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-900">Getting Started Guide</p>
                <p className="text-sm text-gray-500">
                  {isEnrolled
                    ? 'You completed or dismissed the getting started guide.'
                    : 'The getting started guide will appear on your dashboard.'}
                </p>
              </div>
              {isEnrolled && (
                <Button size="sm" variant="secondary" onClick={handleResetOnboarding}>
                  Show Again
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
